import type {
  BackupDeviceKind,
  ConnectionMode,
  ContinuousVoltageBasis,
  CoordinationDeclaration,
  EarthingSystem,
  LocationKind,
  LpsPresence,
  ProspectiveCurrentBasis,
  ProtectionLevelBasis,
  SpdType,
  UpBasis,
} from "../services/surgeProtectionContract";
import type { ValidationLabels } from "../utils/validationMessages";

// The words of the surge protection study (EOS-10, Master Prompt A35): the form's labels, keyed
// by the request path with list indexes as "*", and the label maps of the controlled vocabularies
// the panels print. Exhaustive maps: a new member without a label is a compile error.

export const SPD_TYPE_LABELS: Record<SpdType, string> = {
  TYPE_1: "Type 1",
  TYPE_2: "Type 2",
  TYPE_1_2: "Type 1 + 2 (combined)",
};

export const LOCATION_LABELS: Record<LocationKind, string> = {
  SERVICE_ENTRANCE: "Service entrance (main board)",
  DISTRIBUTION_BOARD: "Distribution board",
  FINAL_BOARD: "Final board",
};

export const EARTHING_SYSTEM_LABELS: Record<EarthingSystem, string> = {
  TN_C: "TN-C",
  TN_S: "TN-S",
  TN_C_S: "TN-C-S",
  TT: "TT",
  IT: "IT",
};

// The declared vocabulary of CTO decision (b) of 2026-10-18; no earthing-versus-mode rule.
export const CONNECTION_MODE_LABELS: Record<ConnectionMode, string> = {
  FOUR_PLUS_ZERO: "4+0",
  THREE_PLUS_ZERO: "3+0",
  THREE_PLUS_ONE: "3+1",
  TWO_PLUS_ZERO: "2+0",
  ONE_PLUS_ONE: "1+1",
};

export const LPS_LABELS: Record<LpsPresence, string> = {
  PRESENT: "Lightning protection system present",
  NONE: "No lightning protection system",
  UNKNOWN: "Lightning protection system unknown",
};

export const UP_BASIS_LABELS: Record<UpBasis, string> = {
  AT_IN: "at In",
  AT_IIMP: "at Iimp",
  NOT_STATED: "current not stated",
};

export const BACKUP_DEVICE_KIND_LABELS: Record<BackupDeviceKind, string> = {
  FUSE: "Fuse",
  MCB: "MCB",
  MCCB: "MCCB",
  ACB: "ACB",
  INTEGRATED: "Integrated in the SPD",
};

export const COORDINATION_LABELS: Record<CoordinationDeclaration, string> = {
  DECLARED_BY_MANUFACTURER: "Declared by the manufacturer",
  NOT_DECLARED: "Not declared",
};

export const PROTECTION_LEVEL_BASIS_LABELS: Record<ProtectionLevelBasis, string> = {
  DECLARED_LEAD_VOLTAGE: "Declared lead voltage",
  COMPUTED_FROM_LENGTH: "Lead length × declared drop",
  NOT_ESTABLISHED: "Not established (no leads declared)",
};

export const CONTINUOUS_VOLTAGE_BASIS_LABELS: Record<ContinuousVoltageBasis, string> = {
  DECLARED_MINIMUM: "Declared minimum Uc",
  NOT_ESTABLISHED: "Uc stated, no minimum declared",
};

export const PROSPECTIVE_CURRENT_BASIS_LABELS: Record<ProspectiveCurrentBasis, string> = {
  LINKED_RUN: "Linked fault run (Ik'')",
  DECLARED: "Declared current",
  NOT_ESTABLISHED: "Not established",
};

export const SURGE_PROTECTION_LABELS: ValidationLabels = {
  fields: {
    "study.code": "Study code",
    "study.name": "Study name",
    "study.spd_code": "SPD code",
    "study.location_kind": "Location",
    "study.system_voltage_v": "System voltage",
    "study.system_voltage_v.value": "System voltage (V)",
    "study.system_voltage_v.source": "System voltage source",
    "study.earthing_system": "Earthing system",
    "study.connection_mode": "Connection mode",
    "study.connection_mode_source": "Connection mode source",
    "study.sld_bus_code": "Bus (SLD)",
    "study.fault_run_id": "Fault run",
    "study.prospective_current": "Prospective current",
    "study.prospective_current.value": "Prospective current (kA)",
    "study.prospective_current.source": "Prospective current source",
    "study.lps_presence": "Lightning protection system",
    "study.spd": "SPD",
    "study.spd.spd_type": "SPD type",
    "study.spd.up_kv": "Voltage protection level Up",
    "study.spd.up_kv.value": "Up (kV)",
    "study.spd.up_kv.source": "Up source",
    "study.spd.up_basis": "Up stated at",
    "study.spd.uc_v": "Continuous operating voltage Uc",
    "study.spd.uc_v.value": "Uc (V)",
    "study.spd.uc_v.source": "Uc source",
    "study.spd.ut_v": "TOV withstand UT",
    "study.spd.ut_v.value": "UT (V)",
    "study.spd.ut_v.source": "UT source",
    "study.spd.iimp_ka": "Impulse current Iimp",
    "study.spd.iimp_ka.value": "Iimp (kA)",
    "study.spd.iimp_ka.source": "Iimp source",
    "study.spd.in_ka": "Nominal discharge current In",
    "study.spd.in_ka.value": "In (kA)",
    "study.spd.in_ka.source": "In source",
    "study.spd.imax_ka": "Maximum discharge current Imax",
    "study.spd.imax_ka.value": "Imax (kA)",
    "study.spd.imax_ka.source": "Imax source",
    "study.spd.iimp_per_mode_declared": "Iimp declared per mode",
    "study.spd.isccr_ka": "Short-circuit current rating",
    "study.spd.isccr_ka.value": "Short-circuit current rating (kA)",
    "study.spd.isccr_ka.source": "Short-circuit rating source",
    "study.spd.backup_device": "Back-up device",
    "study.spd.backup_device.kind": "Back-up device kind",
    "study.spd.backup_device.rating_a": "Back-up device rating (A)",
    "study.spd.backup_device.source": "Back-up device source",
    "study.spd.backup_device.sld_device_code": "Back-up device (SLD)",
    "study.spd.protection_distance_m": "Protection distance",
    "study.spd.protection_distance_m.value": "Protection distance (m)",
    "study.spd.protection_distance_m.source": "Protection distance source",
    "study.spd.coordination": "Energy coordination",
    "study.spd.coordination_source": "Coordination source",
    "study.spd.coordinated_spd_reference": "Coordinated SPD",
    "study.spd.manufacturer_reference": "Manufacturer reference",
    "study.leads": "Connecting leads",
    "study.leads.lead_voltage_kv": "Lead voltage drop",
    "study.leads.lead_voltage_kv.value": "Lead voltage drop (kV)",
    "study.leads.lead_voltage_kv.source": "Lead voltage source",
    "study.leads.lead_length_m": "Lead length",
    "study.leads.lead_length_m.value": "Lead length (m)",
    "study.leads.lead_length_m.source": "Lead length source",
    "study.leads.lead_inductive_drop_kv_per_m": "Inductive drop",
    "study.leads.lead_inductive_drop_kv_per_m.value": "Inductive drop (kV/m)",
    "study.leads.lead_inductive_drop_kv_per_m.source": "Inductive drop source",
    "study.requirements": "Requirements",
    "study.requirements.uc_minimum_v": "Minimum Uc",
    "study.requirements.uc_minimum_v.value": "Minimum Uc (V)",
    "study.requirements.uc_minimum_v.source": "Minimum Uc source",
    "study.requirements.ut_required_v": "Required UT",
    "study.requirements.ut_required_v.value": "Required UT (V)",
    "study.requirements.ut_required_v.source": "Required UT source",
    "study.requirements.iimp_required_ka": "Required Iimp",
    "study.requirements.iimp_required_ka.value": "Required Iimp (kA)",
    "study.requirements.iimp_required_ka.source": "Required Iimp source",
    "study.requirements.in_required_ka": "Required In",
    "study.requirements.in_required_ka.value": "Required In (kA)",
    "study.requirements.in_required_ka.source": "Required In source",
    "study.requirements.imax_required_ka": "Required Imax",
    "study.requirements.imax_required_ka.value": "Required Imax (kA)",
    "study.requirements.imax_required_ka.source": "Required Imax source",
    "study.items": "Protected items",
    "study.items.*.code": "Item code",
    "study.items.*.name": "Item name",
    "study.items.*.uw_kv": "Rated impulse withstand Uw",
    "study.items.*.uw_kv.value": "Uw (kV)",
    "study.items.*.uw_kv.source": "Uw source",
    "study.items.*.distance_m": "Distance from the SPD (m)",
    "study.items.*.notes": "Item notes",
    "study.upstream_spd_code": "Upstream SPD",
    "study.downstream_spd_code": "Downstream SPD",
    "study.jurisdiction_profile": "Jurisdiction profile",
    "study.notes": "Notes",
    project_revision_id: "Project revision",
    notes: "Run notes",
  },
  groups: {
    items: "Item",
  },
};
